import React from 'react';
import { ShieldCheck, FlaskConical, Scale, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function Methodology() {
  const {
    t,
    language
  } = useLanguage();
  const methodology = t.methodology as any;
  const stats = methodology.cards || [];
  const label = methodology.label || 'Trust & Transparency';
  const icons = [FlaskConical, Scale, MessageSquare, ShieldCheck];
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useScrollAnimation({
    threshold: 0.2
  });
  return <section id="methodology" className="py-24 bg-[#050505]" aria-labelledby="methodology-heading" itemScope itemType="https://schema.org/WebPageElement" data-id="element-277">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-278">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start" data-id="element-279">
          {/* Left Column — SEO: heading hierarchy h2 > p */}
          <header ref={leftRef} className={`w-full lg:w-5/12 animate-hidden animate-slide-right ${leftVisible ? 'animate-visible' : ''}`} data-id="element-280">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-281">
              <div className="p-2 rounded-lg bg-accent-blue/10 text-accent-blue" aria-hidden="true" data-id="element-282">
                <ShieldCheck className="w-6 h-6" data-id="element-283" />
              </div>
              <span className="text-sm font-bold text-accent-blue uppercase tracking-wider" itemProp="alternativeHeadline" data-id="element-284">
                {label}
              </span>
            </div>
            <h2 id="methodology-heading" className="text-3xl font-bold text-white mb-4" itemProp="name" data-id="element-285">
              {methodology.title}
            </h2>
            <p className="text-gray-300 mb-8 leading-relaxed" itemProp="description" data-id="element-286">
              {methodology.description}
            </p>
          </header>

          {/* Right Column — SEO: ordered list of criteria */}
          <div className="w-full lg:w-7/12" role="list" aria-label={methodology.title} data-id="element-287">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-288">
              {stats.map((card: {
              title: string;
              desc: string;
            }, index: number) => <MethodologyCard key={index} card={card} index={index} icons={icons} data-id="element-289" />)}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden semantic content for crawlers that don't execute JS —
             provides the same content in a noscript-accessible way */}
      <noscript data-id="element-290">
        <div style={{
        padding: '2rem',
        color: '#fff',
        background: '#050505'
      }} data-id="element-291">
          <h2 data-id="element-292">{methodology.title}</h2>
          <p data-id="element-293">{methodology.description}</p>
          <ol data-id="element-294">
            {stats.map((card: {
            title: string;
            desc: string;
          }, i: number) => <li key={i} data-id="element-295">
                  <strong data-id="element-296">{card.title}</strong> — {card.desc}
                </li>)}
          </ol>
        </div>
      </noscript>
    </section>;
}
function MethodologyCard({
  card,
  index,
  icons
}: {
  card: {
    title: string;
    desc: string;
  };
  index: number;
  icons: any[];
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const Icon = icons[index] || ShieldCheck;
  const delayClass = `delay-${index * 100}`;
  return <article ref={ref} role="listitem" className={`bg-[#0A0A0A] p-6 rounded-2xl border border-white/10 h-full flex flex-col items-center text-center justify-center animate-hidden animate-fade-up ${isVisible ? 'animate-visible' : ''} ${delayClass}`} itemScope itemType="https://schema.org/ListItem" data-id="element-297">
      <meta itemProp="position" content={String(index + 1)} data-id="element-298" />
      <div className="w-12 h-12 rounded-xl bg-accent-blue/10 flex items-center justify-center mb-4" aria-hidden="true" data-id="element-299">
        <Icon className="w-6 h-6 text-accent-blue" data-id="element-300" />
      </div>
      <h3 className="text-lg font-bold text-white leading-tight mb-2" itemProp="name" data-id="element-301">
        {card.title}
      </h3>
      <p className="text-sm text-gray-400 leading-relaxed" itemProp="description" data-id="element-302">
        {card.desc}
      </p>
    </article>;
}